import { useNavigation } from '@/providers/navigation-provider';
import CreateRounded from '@mui/icons-material/CreateRounded';
import {
    Box,
    ListItemButton as MuiListItemButton,
    ListItemIcon,
    ListItemText,
    Theme,
    useMediaQuery,
    Typography,
    Badge,
    Chip,
    Stack,
    Tooltip,
    Button,
} from '@mui/material';
import { Link, LinkProps, useLocation, matchPath, useSearchParams } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useTeamPermissions } from '@/providers/team-permissions-provider';
import { hasRouteTeamPermission } from '@/utils/permissions/has-route-team-permission';
import type { ListItemButtonProps } from '@mui/material/ListItemButton';
import { BrushRounded } from '@mui/icons-material';
import { useMenu } from '@/providers/menu-provider';
import { canSwitchComposeVersion, isUsingComposeBeta } from '@/utils/beta/version-cohorts';
import { usersPathFilterAccess, isSwonkieDevWorkspace, isFenixExclusive } from '@/utils/users-path-filter';
import { useSession } from '@/providers/session-provider';

const ComposeButton = styled(Button)<LinkProps>(() => ({
    gap: '8px',
    padding: '4px',
    borderRadius: '8px',
    width: '100%',
    borderWidth: 2,
    minWidth: 0,
    height: 38,
}));

export const ListItemButton: React.ComponentType<ListItemButtonProps & { href?: LinkProps['to'] }> = styled(
    MuiListItemButton,
)<{ href?: LinkProps['to'] }>(({ theme }) => ({
    borderRadius: '99px',
    '&.Mui-selected': {
        color: theme.palette.primary.main,
        svg: {
            color: theme.palette.primary.main,
        },
    },
}));

export default function Menu({ showSubmenu }: { showSubmenu: (menu: string) => void }) {
    const { mainNavOpen, setMainNavOpen } = useNavigation();
    const { getMenuPath, menu } = useMenu();
    const { pathname } = useLocation();
    const { t } = useTranslation();
    const { teamPermissions } = useTeamPermissions();
    const { workspace } = useSession();
    const [searchParams] = useSearchParams();

    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

    const usingComposeBeta = isUsingComposeBeta(workspace);

    // Build the compose URL preserving the current view and search params (filters).
    // Mirrors the logic in usePostNavigation.navigateToPostPath but without requiring PostsFiltersContext.
    const composeBasePath = (() => {
        if (pathname.includes('/posts/list')) return '/posts/list/post';
        if (pathname.includes('/posts/kanban')) return '/posts/kanban/post';
        // For custom views, extract the viewId from the path (/posts/:viewId/...)
        const customViewMatch = pathname.match(/^\/posts\/([^/]+)/);
        const viewId = customViewMatch?.[1];
        if (viewId && viewId !== 'calendar' && viewId !== 'list' && viewId !== 'kanban') {
            return `/posts/${viewId}/post`;
        }
        return '/posts/calendar/post';
    })();
    const composeTo = searchParams.toString()
        ? `${composeBasePath}?${searchParams.toString()}`
        : composeBasePath;

    return (
        <Box py={1}>
            {!workspace.isSwonkieDev && (
                <Box
                    display="flex"
                    justifyContent="center"
                    flexDirection={'column'}
                    gap={1}
                    mb={3}
                    px={!mainNavOpen ? 1 : 0}
                >
                    {usingComposeBeta ? (
                        <>
                            <Tooltip
                                title={t('navigation.compose')}
                                arrow
                                placement="right"
                                disableHoverListener={mainNavOpen}
                            >
                                <ComposeButton
                                    data-id="menu-post-planning"
                                    variant={'contained'}
                                    color={'primary'}
                                    LinkComponent={Link}
                                    to={composeTo}
                                >
                                    {mainNavOpen && t('navigation.compose')}
                                    <Badge
                                        color="secondary"
                                        badgeContent={canSwitchComposeVersion(workspace) ? 'β' : null}
                                        invisible={mainNavOpen}
                                    >
                                        <BrushRounded />
                                    </Badge>
                                    {mainNavOpen &&
                                        (isSwonkieDevWorkspace(workspace) || isFenixExclusive(workspace)) && (
                                            <Chip
                                                color="secondary"
                                                label="BETA"
                                                size="small"
                                                sx={(theme) => ({
                                                    position: 'absolute',
                                                    top: '50%',
                                                    transform: 'translateY(-50%)',
                                                    right: theme.spacing(1),
                                                    '.MuiChip-label': { fontWeight: 500 },
                                                })}
                                            />
                                        )}
                                </ComposeButton>
                            </Tooltip>
                        </>
                    ) : (
                        <Tooltip
                            title={t('navigation.compose')}
                            arrow
                            placement="right"
                            disableHoverListener={mainNavOpen}
                        >
                            <ComposeButton
                                variant={'contained'}
                                color={'primary'}
                                LinkComponent={Link}
                                to="/produce-content"
                                data-id="menu-produce-content"
                            >
                                {mainNavOpen && t('navigation.compose')}
                                <CreateRounded />
                            </ComposeButton>
                        </Tooltip>
                    )}
                </Box>
            )}
            <Box display="flex" flexDirection="column" {...(!mainNavOpen && { sx: { px: 1 } })}>
                {menu
                    .filter((item) => !item.hide)
                    .filter((item) => !item.secondary)
                    .filter((item) => hasRouteTeamPermission(item.path, teamPermissions))
                    .filter((item) => usersPathFilterAccess(item.path, workspace))
                    .map((item, key) => (
                        <Tooltip
                            title={t(item.name)}
                            arrow
                            placement="right"
                            disableHoverListener={mainNavOpen}
                            key={key}
                        >
                            <ListItemButton
                                key={item.path}
                                dense
                                href={item.sub && isMobile ? '' : getMenuPath(item)}
                                onClick={() => {
                                    if (item.sub) {
                                        showSubmenu(item.path);
                                    } else if (isMobile) {
                                        setMainNavOpen(false);
                                    }
                                }}
                                selected={!!matchPath({ path: item.path, end: false }, pathname)}
                                sx={{ ...(!mainNavOpen && { justifyContent: 'center', py: 1 }) }}
                                data-id={`menu${getMenuPath(item).replace(/\//g, '-')}`}
                            >
                                <Badge
                                    color={item.betaEnabled ? 'secondary' : 'default'}
                                    badgeContent="β"
                                    invisible={!item.beta || mainNavOpen}
                                    slotProps={{
                                        badge: {
                                            sx: (theme) => ({
                                                ...(!item.betaEnabled && {
                                                    bgcolor: theme.palette.action.selected,
                                                }),
                                            }),
                                        },
                                    }}
                                >
                                    <ListItemIcon sx={{ ...(!mainNavOpen && { minWidth: 0 }) }}>
                                        {item.icon}
                                    </ListItemIcon>
                                </Badge>
                                {mainNavOpen && (
                                    <>
                                        <ListItemText>
                                            <Stack
                                                direction="row"
                                                alignItems="center"
                                                justifyContent="space-between"
                                            >
                                                <Typography noWrap variant="body2">
                                                    {`${item.classic ? `(${t('classic')}) ` : ''}${t(item.name)}`}
                                                </Typography>
                                                {item.beta && (
                                                    <Chip
                                                        color={item.betaEnabled ? 'secondary' : 'default'}
                                                        label="BETA"
                                                        size="small"
                                                        sx={{ '.MuiChip-label': { fontWeight: 500 } }}
                                                    />
                                                )}
                                            </Stack>
                                        </ListItemText>
                                    </>
                                )}
                            </ListItemButton>
                        </Tooltip>
                    ))}
            </Box>
        </Box>
    );
}
